// /visa-matrix — TH interactive Thailand Visa Comparison Matrix
// Phase P-VISA-MATRIX (2026-06). High-intent decision tool comparing 7 visa types.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { VISA_MATRIX, FIT_LABELS, MATRIX_ATTRIBUTES, type FitTag, type VisaRow } from "@/data/visa-matrix";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, Filter, CheckCircle2, XCircle, ChevronDown } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/visa-matrix";
export const TITLE = "เปรียบเทียบวีซ่าไทย 2026 — Tourist/DTV/LTR/Non-O/Non-B/ED Visa Matrix | NYC";
export const DESC = "Visa Matrix เปรียบเทียบวีซ่าไทยครบ 7 ประเภท — DTV 5 ปี, LTR 10 ปี, Non-O Retirement/Marriage, Non-B Work, Tourist, Non-ED — ทีมงานตอบไวทุกวัน ปรึกษาก่อนตัดสินใจได้ฟรี";

export const Route = createFileRoute("/visa-matrix")({
  head: () => {
    const url = `${SITE}${PATH}`;
    return {
      meta: [
        { title: ctaTitle(TITLE, "/visa-matrix", "th")}, { name: "description", content: ctaDesc(DESC, "/visa-matrix", "th")},
        { property: "og:title", content: ctaTitle(TITLE, "/visa-matrix", "th")}, { property: "og:description", content: ctaDesc(DESC, "/visa-matrix", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "เปรียบเทียบวีซ่าไทย, DTV vs LTR, Non-O Retirement, Non-B Work Permit, Thailand visa comparison, วีซ่าระยะยาวไทย, visa matrix" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(PATH, "/en/visa-matrix")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "@id": `${url}#tool`,
              name: "Thailand Visa Comparison Matrix",
              applicationCategory: "BusinessApplication",
              operatingSystem: "Any",
              url,
              inLanguage: "th-TH",
              offers: { "@type": "Offer", price: "0", priceCurrency: "THB" },
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "ItemList", "@id": `${url}#list`,
              name: "Thailand visa types compared",
              numberOfItems: VISA_MATRIX.length,
              itemListElement: VISA_MATRIX.map((v, i) => ({
                "@type": "ListItem", position: i + 1, name: v.nameTh, url: `${SITE}${v.detailPath.th}`,
              })),
            }),
          },
          faqPageSchema([
            { q: "วีซ่าไทยแบบไหนเหมาะกับ Digital Nomad?", a: "DTV (Destination Thailand Visa) — วีซ่า 5 ปี multiple entry พักได้ 180 วัน/ครั้ง ต่อได้อีก 180 วัน รวม 360 วัน/ปี ค่าธรรมเนียม 10,000 บาท ต้องโชว์เงิน 500,000 บาท ทำงานออนไลน์ให้บริษัทต่างประเทศได้" },
            { q: "LTR กับ DTV ต่างกันอย่างไร?", a: "LTR วีซ่า 10 ปีของ BOI เหมาะกับ HNWI/Pensioner/Senior Pro/HSP รายได้ ≥ USD 80K/ปี ได้ Digital Work Permit + ภาษี foreign income ยกเว้น ส่วน DTV เป็นวีซ่า 5 ปีของ MFA สำหรับ Digital Nomad ทั่วไป เงื่อนไขการเงินต่ำกว่าและไม่มีสิทธิทำงานในบริษัทไทย" },
            { q: "อายุ 50+ ต้องใช้วีซ่าอะไร?", a: "Non-O Retirement หรือ LTR Wealthy Pensioner — Non-O ต้องโชว์เงินในบัญชีไทย 800,000 บาทหรือรายได้ 65,000 บาท/เดือน ต่อปีต่อปี ส่วน LTR Pensioner ต้องมีรายได้ ≥ USD 80K/ปี วีซ่า 10 ปี" },
            { q: "วีซ่าไหนทำงานในไทยได้ถูกกฎหมาย?", a: "LTR (Digital Work Permit อัตโนมัติ), Non-B + Work Permit (ลูกจ้างบริษัทไทย) และ Non-O Marriage (ขอ Work Permit แยก) DTV ทำงานออนไลน์ให้บริษัทต่างประเทศได้ แต่ทำงานในบริษัทไทยไม่ได้ Tourist/ED ห้ามทำงานเด็ดขาด" },
            { q: "ต้องเตรียมเงินเท่าไหร่สำหรับวีซ่าระยะยาว?", a: "DTV 500,000 บาท · Non-O Marriage 400,000 บาทหรือ 40,000/เดือน · Non-O Retirement 800,000 บาทหรือ 65,000/เดือน · LTR USD 80,000+/ปีหรือสินทรัพย์ USD 1M+ (ตามกลุ่ม)" },
          ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "Visa Matrix", item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: VisaMatrixTh,
});

function VisaMatrixTh() {
  const [selectedFit, setSelectedFit] = useState<FitTag | "all">("all");
  const [workOnly, setWorkOnly] = useState(false);
  const [openMobile, setOpenMobile] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return VISA_MATRIX.filter((v) => {
      if (workOnly && !v.workAllowed) return false;
      if (selectedFit !== "all" && !v.fits.includes(selectedFit)) return false;
      return true;
    });
  }, [selectedFit, workOnly]);

  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-14 pb-10">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <span className="mx-2">/</span><span>Visa Matrix</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Sparkles className="mr-1 inline h-3 w-3" /> Interactive Tool · 7 Thai visas
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">เปรียบเทียบวีซ่าไทย — Visa Matrix 2026</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            เลือกวีซ่าไทยที่ตรงกับ profile ของคุณภายใน 60 วินาที · เปรียบเทียบ 7 ประเภท (Tourist, DTV, LTR, Non-O Retirement, Non-O Marriage, Non-B Work, Non-ED) ทุกมิติ: อายุวีซ่า, เงินขั้นต่ำ, สิทธิทำงาน, ภาษี, ครอบครัว
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">วีซ่าไทยแบบไหนเหมาะกับฉัน?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              สรุปสั้น: <b>Digital Nomad → DTV</b> (5 ปี, 500K บาท), <b>HNWI/รายได้สูง/HSP → LTR</b> (10 ปี, USD 80K+/ปี, ภาษี 17%), <b>เกษียณ 50+ → Non-O Retirement</b> (800K บาท), <b>คู่สมรสคนไทย → Non-O Marriage</b> (400K บาท + Work Permit แยก), <b>ลูกจ้างบริษัทไทย → Non-B + Work Permit</b>, <b>นักเรียน/Muay Thai → Non-ED</b>, <b>เที่ยว ≤ 60 วัน → Tourist Visa</b>
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border bg-card/50 px-4 py-5 sticky top-0 z-20 backdrop-blur">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-foreground/80">
              <Filter className="h-3.5 w-3.5" /> กรอง:
            </div>
            <button
              onClick={() => setSelectedFit("all")}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${selectedFit === "all" ? "bg-primary text-primary-foreground" : "border border-border bg-background hover:border-primary"}`}
            >ทั้งหมด</button>
            {(Object.keys(FIT_LABELS) as FitTag[]).map((tag) => (
              <button
                key={tag}
                onClick={() => setSelectedFit(tag)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${selectedFit === tag ? "bg-primary text-primary-foreground" : "border border-border bg-background hover:border-primary"}`}
              >{FIT_LABELS[tag].th}</button>
            ))}
            <label className="ml-auto inline-flex items-center gap-2 text-xs font-semibold cursor-pointer">
              <input type="checkbox" checked={workOnly} onChange={(e) => setWorkOnly(e.target.checked)} className="h-4 w-4 accent-primary" />
              เฉพาะที่ทำงานได้
            </label>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">แสดง {filtered.length}/{VISA_MATRIX.length} ประเภท</div>
        </div>
      </section>

      {/* Desktop matrix table */}
      <section className="px-4 py-8 hidden lg:block">
        <div className="mx-auto max-w-6xl overflow-x-auto">
          <table className="w-full min-w-[920px] border-collapse text-sm">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-background border-b border-border px-3 py-3 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">คุณสมบัติ</th>
                {filtered.map((v) => (
                  <th key={v.slug} className="border-b border-border px-3 py-3 text-left align-top min-w-[170px]">
                    <div className="text-2xl">{v.icon}</div>
                    <div className="mt-1 text-sm font-extrabold">{v.nameTh}</div>
                    <div className="mt-0.5 text-[11px] font-normal text-muted-foreground line-clamp-2">{v.taglineTh}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIX_ATTRIBUTES.map((attr) => (
                <tr key={attr.key} className="border-b border-border/50 hover:bg-muted/30">
                  <td className="sticky left-0 z-10 bg-background px-3 py-3 text-xs font-bold text-foreground">{attr.labelTh}</td>
                  {filtered.map((v) => (
                    <td key={v.slug} className="px-3 py-3 align-top text-xs leading-relaxed">
                      {attr.key === "entries" ? (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${v.entries === "multiple" ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"}`}>
                          {v.entries === "multiple" ? "Multiple" : "Single"}
                        </span>
                      ) : attr.key === "workTh" ? (
                        <div className="flex items-start gap-1.5">
                          {v.workAllowed ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />}
                          <span>{v.workTh}</span>
                        </div>
                      ) : (
                        <span>{String(v[attr.key as keyof VisaRow])}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-primary/5">
                <td className="sticky left-0 z-10 bg-primary/5 px-3 py-3 text-xs font-bold text-foreground">ค่าบริการ NYC</td>
                {filtered.map((v) => (
                  <td key={v.slug} className="px-3 py-3 text-sm font-extrabold text-primary">
                    ฿{v.servicePriceTHB.toLocaleString()}
                    <div className="mt-0.5 text-[10px] font-normal text-muted-foreground">+ ค่าธรรมเนียมราชการ</div>
                  </td>
                ))}
              </tr>
              <tr>
                <td className="sticky left-0 z-10 bg-background px-3 py-3"></td>
                {filtered.map((v) => (
                  <td key={v.slug} className="px-3 py-3">
                    <Link to={v.detailPath.th as never} className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground hover:opacity-90">
                      ดูรายละเอียด <ArrowRight className="h-3 w-3" />
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Mobile cards */}
      <section className="px-4 py-8 lg:hidden">
        <div className="mx-auto max-w-2xl space-y-3">
          {filtered.map((v) => (
            <div key={v.slug} className="rounded-2xl border border-border bg-card">
              <button
                onClick={() => setOpenMobile(openMobile === v.slug ? null : v.slug)}
                className="flex w-full items-start gap-3 p-4 text-left"
              >
                <span className="text-2xl">{v.icon}</span>
                <div className="flex-1">
                  <div className="font-extrabold text-sm">{v.nameTh}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{v.taglineTh}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold">{v.durationTh}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${v.workAllowed ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-rose-100 text-rose-900 dark:bg-rose-900/40 dark:text-rose-200"}`}>
                      {v.workAllowed ? "ทำงานได้" : "ห้ามทำงาน"}
                    </span>
                    <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold text-primary">฿{v.servicePriceTHB.toLocaleString()}</span>
                  </div>
                </div>
                <ChevronDown className={`h-4 w-4 shrink-0 transition ${openMobile === v.slug ? "rotate-180" : ""}`} />
              </button>
              {openMobile === v.slug && (
                <div className="border-t border-border px-4 py-3 text-xs space-y-2">
                  <p className="leading-relaxed text-muted-foreground">{v.quickAnswerTh}</p>
                  <dl className="mt-3 grid grid-cols-1 gap-x-3 gap-y-2 sm:grid-cols-2">
                    {MATRIX_ATTRIBUTES.filter((a) => a.key !== "entries").map((attr) => (
                      <div key={attr.key}>
                        <dt className="font-bold text-foreground/80">{attr.labelTh}</dt>
                        <dd className="text-muted-foreground">{String(v[attr.key as keyof VisaRow])}</dd>
                      </div>
                    ))}
                  </dl>
                  <Link to={v.detailPath.th as never} className="mt-3 inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground">
                    ดูรายละเอียดเต็ม <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* AI quick-answers grid */}
      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">สรุปวีซ่าแต่ละประเภท</h2>
          <p className="mt-2 text-sm text-muted-foreground">เนื้อหาสั้น (~ 60 คำ) ที่ AI เช่น ChatGPT, Gemini, Perplexity ใช้สรุปคำตอบได้</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {VISA_MATRIX.map((v) => (
              <div key={v.slug} className="rounded-2xl border border-border bg-card p-5 aeo-speakable" data-speakable="true">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{v.icon}</span>
                  <h3 className="text-sm font-extrabold">{v.nameTh}</h3>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{v.quickAnswerTh}</p>
                <Link to={v.detailPath.th as never} className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline">
                  อ่านต่อ <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-10">
        <div className="mx-auto max-w-5xl rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-6 sm:p-8">
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-block rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">AI Wizard · 60 วินาที</span>
              <h2 className="mt-2 text-xl font-extrabold sm:text-2xl">ไม่แน่ใจว่าวีซ่าไหนเหมาะ? ลองตอบ 5 คำถาม</h2>
              <p className="mt-1 text-sm text-muted-foreground">ระบบจะให้คะแนนวีซ่า 7 ประเภทพร้อมเหตุผลและค่าธรรมเนียม</p>
            </div>
            <Link to="/visa-wizard" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              เริ่มทำ Wizard <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">

        <div className="mx-auto max-w-5xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">บริการเสริมที่เกี่ยวข้อง</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "DTV (Digital Nomad)", d: "วีซ่า 5 ปี 9 หมวด", to: "/dtv" as const },
              { t: "LTR (BOI 10 ปี)", d: "HSP ภาษี 17% · Pensioner · HNWI", to: "/ltr" as const },
              { t: "TM30 / 90-day Compliance", d: "รายงานตัว · แจ้งที่พัก · Re-entry", to: "/visa-compliance" as const },
              { t: "Work Permit", d: "ลูกจ้างบริษัทไทยทุกสัญชาติ", to: "/work-permit" as const },
              { t: "Company Setup", d: "Thai Limited · BOI · Branch · Rep", to: "/company-setup" as const },
              { t: "Apostille / MFA", d: "รับรองเอกสารต่างประเทศ", to: "/mfa-legalisation" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary transition">
                <div className="font-bold text-sm">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    </main>
  );
}
